import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useOperations } from "@/contexts/OperationsContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import NotificationsPopover from "@/components/NotificationsPopover";
import ReportIssueButton from "@/components/ReportIssueButton";
import AiDisclaimer from "@/components/AiDisclaimer";
import MaitreLeslexChat from "@/components/MaitreLeslexChat";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { createWebSocket } from "@/lib/api";
import { useMyReportsUnreadCount, useAdminReportsUnreadCount } from "@/hooks/useReportsUnreadCount";
import {
  LayoutDashboard, FileText, MessageSquare, FolderOpen, History,
  Scale, Menu, ChevronLeft, LogOut, FileStack, User, Settings, Crown,
  Search, Database, BarChart3, Command, TrendingUp, Bot, Calculator, Shield, Building2, Handshake, Share2, Trash2, Mail, Loader2, Lightbulb, Bug, MessageCircle, Radar,
} from "lucide-react";

const sidebarItems = [
  { labelKey: "nav.dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { labelKey: "nav.analytics", icon: TrendingUp, path: "/analytics" },
  { labelKey: "nav.legal_search", icon: Search, path: "/legal-search" },
  { labelKey: "nav.advice", icon: Lightbulb, path: "/advice" },
  { labelKey: "nav.prediction", icon: TrendingUp, path: "/prediction" },
  { labelKey: "nav.contract_analysis", icon: Shield, path: "/contract-analysis" },
  { labelKey: "nav.court_portal", icon: Building2, path: "/court-portal" },
  { labelKey: "nav.templates", icon: FolderOpen, path: "/templates" },
  { labelKey: "nav.calculators", icon: Calculator, path: "/calculators" },
  { labelKey: "nav.due_diligence", icon: Building2, path: "/due-diligence" },
  { labelKey: "nav.detective", icon: Radar, path: "/detective" },
  { labelKey: "nav.benchmark", icon: BarChart3, path: "/benchmark" },
  { labelKey: "nav.knowledge_base", icon: Database, path: "/knowledge-base" },
  { labelKey: "nav.knowledge_graph", icon: Share2, path: "/knowledge-graph" },
  { labelKey: "nav.negotiation", icon: Handshake, path: "/negotiation" },
  { labelKey: "nav.compliance", icon: Shield, path: "/compliance" },
  { labelKey: "nav.global_history", icon: History, path: "/history" },
  { labelKey: "nav.my_reports", icon: MessageCircle, path: "/mes-signalements", badgeKey: "userUnread" },
  { labelKey: "nav.api", icon: Command, path: "/developer" },
  { labelKey: "nav.trash", icon: Trash2, path: "/trash" },
  { labelKey: "nav.inbox", icon: Mail, path: "/inbox" },
  { labelKey: "nav.admin_inbox", icon: Mail, path: "/admin/inbox", ownerOnly: true },
  { labelKey: "nav.admin", icon: Crown, path: "/admin", adminOnly: true },
  { labelKey: "nav.admin_reports", icon: Bug, path: "/admin/reports", adminOnly: true, badgeKey: "adminUnread" },
  { labelKey: "nav.settings", icon: Settings, path: "/settings" },
];

const projectItems = [
  { labelKey: "nav.creator", icon: FileText, path: "creator" },
  { labelKey: "nav.studio", icon: MessageSquare, path: "studio" },
  { labelKey: "nav.sources", icon: FolderOpen, path: "sources" },
  { labelKey: "nav.history", icon: History, path: "history" },
  { labelKey: "nav.pdf_lab", icon: FileStack, path: "pdf-lab" },
  { labelKey: "nav.agent_ai", icon: Bot, path: "agent" },
];

function SidebarContent({ collapsed, projectId, currentPath, onNavigate }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const isProjectView = projectId != null;
  const isAdmin = ["owner", "admin"].includes(user?.role);
  const userUnread = useMyReportsUnreadCount();
  const adminUnread = useAdminReportsUnreadCount(isAdmin);

  const handleClick = (e, path) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(path);
    }
  };

  const getBadgeCount = (badgeKey) => {
    if (badgeKey === "userUnread") return userUnread;
    if (badgeKey === "adminUnread") return adminUnread;
    return 0;
  };

  return (
    <div className="flex flex-col h-full bg-primary text-primary-foreground">
      {/* Logo */}
      <div className="p-4 flex items-center gap-2">
        <Scale className="h-6 w-6 text-gold shrink-0" />
        {!collapsed && <span className="font-heading text-lg font-bold text-white">LES-LEX</span>}
      </div>
      <Separator className="bg-white/10" />

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="space-y-0.5 px-2">
          {sidebarItems.filter((item) => {
                if (item.ownerOnly) return user?.role === "owner";
                if (item.adminOnly) return ["owner", "admin"].includes(user?.role);
                return true;
              }).map((item) => {
            const active = currentPath === item.path || currentPath.startsWith(item.path + "/");
            const badgeCount = item.badgeKey ? getBadgeCount(item.badgeKey) : 0;
            return (
              <Link key={item.path} to={item.path} onClick={(e) => handleClick(e, item.path)}>
                <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-gold/20 text-gold"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}>
                  <div className="relative shrink-0">
                    <item.icon className={`h-4 w-4 ${active ? "text-gold" : ""}`} />
                    {badgeCount > 0 && collapsed && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-2 w-2 rounded-full bg-red-500" />
                    )}
                  </div>
                  {!collapsed && (
                    <>
                      <span className="flex-1">{t(item.labelKey)}</span>
                      {badgeCount > 0 && (
                        <Badge className="bg-red-500 text-white h-5 min-w-[20px] flex items-center justify-center px-1 text-[10px]">
                          {badgeCount > 99 ? "99+" : badgeCount}
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </Link>
            );
          })}

          {isProjectView && (
            <>
              <Separator className="my-3 bg-white/10" />
              <p className={`px-3 mb-2 text-xs font-medium text-white/40 uppercase tracking-wider ${collapsed ? "hidden" : ""}`}>
                {t("nav.project")}
              </p>
              {projectItems.map((item) => {
                const fullPath = `/project/${projectId}/${item.path}`;
                const active = currentPath === fullPath;
                return (
                  <Link key={item.path} to={fullPath} onClick={(e) => handleClick(e, fullPath)}>
                    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      active
                        ? "bg-gold/20 text-gold"
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`} data-testid={`project-tab-${item.path}`}>
                      <item.icon className={`h-4 w-4 shrink-0 ${active ? "text-gold" : ""}`} />
                      {!collapsed && <span>{t(item.labelKey)}</span>}
                    </div>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Search hint */}
        {!collapsed && (
          <div className="px-4 mt-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/40 text-xs border border-white/10">
              <Command className="h-3 w-3" />
              <span>{t("nav.search_hint")}</span>
            </div>
          </div>
        )}
      </ScrollArea>

      {/* User info + logout */}
      <Separator className="bg-white/10" />
      <div className="p-2 space-y-1">
        {user && !collapsed && (
          <div className="px-3 py-2 text-xs text-white/50 space-y-1">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5" />
              <span className="truncate text-white/70">{user.name || user.email}</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {user.plan && user.plan !== "free" && (
                <div className="flex items-center gap-1 text-gold">
                  <Crown className="h-3 w-3" />
                  <span className="text-[10px] font-medium uppercase">{user.plan}</span>
                </div>
              )}
              {user.role && (
                <Badge className="text-[9px] px-1.5 py-0 bg-white/10 text-white/70 border-white/20">
                  {t(`roles.${user.role}`, { defaultValue: user.role })}
                </Badge>
              )}
            </div>
          </div>
        )}
        <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-white/10 hover:text-white transition-all duration-200">
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>{t("actions.logout")}</span>}
        </button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [wsToast, setWsToast] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const wsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const projectMatch = location.pathname.match(/\/project\/([^/]+)/);
  const projectId = projectMatch ? projectMatch[1] : null;

  // Global operations tracking + navigation guard
  const { hasRunning, runningList } = useOperations();

  // Browser refresh/close protection
  useEffect(() => {
    if (!hasRunning) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasRunning]);

  // Navigation handler — intercepts sidebar clicks when operations are running
  const handleNavigate = useCallback((path) => {
    if (hasRunning) {
      setPendingNavigation(path);
    } else {
      navigate(path);
    }
  }, [hasRunning, navigate]);

  // WebSocket for real-time notifications
  const handleWsMessage = useCallback((event, data) => {
    if (event === "notification" || event === "document_ready" || event === "deadline_alert") {
      setWsToast({ title: data.title || "Notification", message: data.message || "" });
      setTimeout(() => setWsToast(null), 5000);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("les_lex_token");
    if (token && !wsRef.current) {
      wsRef.current = createWebSocket(handleWsMessage);
    }
    return () => {
      if (wsRef.current) {
        if (wsRef.current._cleanup) {
          wsRef.current._cleanup();
        } else {
          wsRef.current.close();
        }
        wsRef.current = null;
      }
    };
  }, [handleWsMessage]);

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}>
        <SidebarContent collapsed={collapsed} projectId={projectId} currentPath={location.pathname} onNavigate={handleNavigate} />
        <div className="bg-primary">
          <button onClick={() => setCollapsed(!collapsed)} className="p-2 mx-2 mb-2 rounded-lg text-white/40 hover:bg-white/10 hover:text-white transition-all duration-200">
            <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </div>
      </aside>

      {/* Mobile menu */}
      <Sheet>
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-primary px-4 py-3 flex items-center gap-3">
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white/80 hover:bg-white/10 hover:text-white">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <Scale className="h-5 w-5 text-gold" />
          <span className="font-heading font-bold text-white">LES-LEX</span>
          <div className="ml-auto">
            <NotificationsPopover />
          </div>
        </div>
        <SheetContent side="left" className="w-60 p-0 border-r-0">
          <SidebarContent collapsed={false} projectId={projectId} currentPath={location.pathname} onNavigate={handleNavigate} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main className="flex-1 min-h-screen md:pt-0 pt-14">
        {/* Top bar — glassmorphic */}
        <div className="hidden md:flex items-center justify-end px-6 py-2 border-b bg-card/80 backdrop-blur-md">
          {hasRunning && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 px-2.5 py-1 rounded-full mr-2 animate-pulse">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>{runningList.length} {runningList.length === 1 ? "operation" : "operations"}</span>
            </div>
          )}
          <NotificationsPopover />
        </div>
        <Outlet />
        {/* Global AI disclaimer (legal — RC Pro, loi 71-1130) */}
        <AiDisclaimer variant="compact" className="mt-8 mb-4 border-t pt-4" />
      </main>

      {/* Report Issue floating button (bottom-right) */}
      <ReportIssueButton />
      {/* Maître LES-LEX AI Assistant (bottom-left) */}
      <MaitreLeslexChat />
      {/* PWA Install Prompt + Offline indicator */}
      <PWAInstallPrompt />

      {/* WebSocket toast notification */}
      {wsToast && (
        <div className="fixed bottom-4 right-24 z-50 animate-in slide-in-from-bottom-4 bg-card border border-border rounded-lg shadow-card p-4 max-w-sm">
          <p className="text-sm font-semibold">{wsToast.title}</p>
          {wsToast.message && <p className="text-xs text-muted-foreground mt-1">{wsToast.message}</p>}
        </div>
      )}

      {/* Navigation guard — shown when user tries to navigate with running operations */}
      <AlertDialog open={pendingNavigation !== null} onOpenChange={(open) => { if (!open) setPendingNavigation(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_dialog.operation_in_progress")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <ul className="mt-2 list-disc pl-4 space-y-1">
                  {runningList.map((op, i) => <li key={i}>{op.label}</li>)}
                </ul>
                <p className="mt-3">{t("confirm_dialog.unsaved_changes")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingNavigation(null)}>{t("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              const target = pendingNavigation;
              setPendingNavigation(null);
              navigate(target);
            }}>{t("actions.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
